import type { TableColumnsType } from "antd";
import { useQuery } from "react-query";
import { formatNumber } from "utils/functions";
import { getAccountTimeFormat } from "utils/timeUtil";
import type { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  EVENT_HISTORY_CHANGE_UNIT,
  EVENT_HISTORY_TYPE,
  PLAN_START_TERM,
} from "api/interfaces/subscription.interface";
import type { EventHistoryDto } from "api/interfaces/planInterface.interface";
import * as s from "./EventToHistoryStyled";
import {
  LEVEL_TYPE,
  type BasePagination,
} from "api/interfaces/commonInterface.interface";
import { getPlanEventHistory } from "api/planAPI";
import type { GetPlanEventHistoryResponse } from "api/interfaces/planInterface.interface";
import { changeSortOrderAntd } from "components/atoms/table/AntdTable";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import AccountInfoLabel from "components/atoms/text/labels/AccountInfoLabel";
import { formatPrice } from "utils/priceUtils";

export const eventLabelMap: Record<EVENT_HISTORY_TYPE, string> = {
  [EVENT_HISTORY_TYPE.CHANNEL_ADD]: "Channels Added",
  [EVENT_HISTORY_TYPE.CHANNEL_DEL]: "Channels Removed",
  [EVENT_HISTORY_TYPE.STORAGE_ADD]: "Storage Added",
  [EVENT_HISTORY_TYPE.STORAGE_DEL]: "Storage Removed",
  [EVENT_HISTORY_TYPE.ORG_ADD]: "Enrolled",
  [EVENT_HISTORY_TYPE.ORG_DEL]: "Deactivated",
  [EVENT_HISTORY_TYPE.ORG_SCHE_ADD]: "Activated",
  [EVENT_HISTORY_TYPE.PLAN_START]: "Plan Started",
  [EVENT_HISTORY_TYPE.PLAN_CANCEL]: "Plan Canceled",
  [EVENT_HISTORY_TYPE.PAYMENT]: "Payment",
  [EVENT_HISTORY_TYPE.USAGE_MONTH]: "Usage Changed",
};

export const getChangeUnit = (
  type: EVENT_HISTORY_TYPE
): EVENT_HISTORY_CHANGE_UNIT => {
  if (
    type === EVENT_HISTORY_TYPE.CHANNEL_ADD ||
    type === EVENT_HISTORY_TYPE.CHANNEL_DEL
  ) {
    return EVENT_HISTORY_CHANGE_UNIT.CHANNEL;
  }
  if (
    type === EVENT_HISTORY_TYPE.STORAGE_ADD ||
    type === EVENT_HISTORY_TYPE.STORAGE_DEL
  ) {
    return EVENT_HISTORY_CHANGE_UNIT.STORAGE;
  }
  return EVENT_HISTORY_CHANGE_UNIT.TEXT;
};

export type EventChangeSource = {
  planEventType: EVENT_HISTORY_TYPE;
  change: number;
  accountId?: string;
  recorderId?: string;
  euName?: string;
  euAccountNumber?: string;
  description?: string;
};

export const planStartTermLabelMap: Record<PLAN_START_TERM, string> = {
  [PLAN_START_TERM.MONTHLY]: "monthly",
  [PLAN_START_TERM.YEAR_1]: "1yr",
  [PLAN_START_TERM.YEAR_2]: "2yrs",
  [PLAN_START_TERM.YEAR_3]: "3yrs",
  [PLAN_START_TERM.TEST_MONTHLY]: "2days",
  [PLAN_START_TERM.TEST_YEAR_1]: "3days",
};

export const getOrgChangeLabel = (
  row: EventChangeSource
): JSX.Element | string => {
  const suffix =
    row.planEventType === EVENT_HISTORY_TYPE.ORG_DEL
      ? "Deactivated"
      : "Activated";

  if (typeof row.change === "number" && !Number.isNaN(row.change)) {
    const org =
      row.euAccountNumber && row.euName ? (
        <AccountInfoLabel
          level={LEVEL_TYPE.EU}
          accountNumber={row.euAccountNumber}
          accountName={row.euName}
          direction={"row"}
        />
      ) : (
        `ORG ${Math.abs(row.change)}`
      );
    return org;
  }

  const id = row.recorderId || row.accountId || "ORG";
  return id;
};

export const renderEventType = (value: EVENT_HISTORY_TYPE) => (
  <s.EventText>{eventLabelMap[value] ?? value}</s.EventText>
);

const NON_QUANTITATIVE_EVENT_TYPES = [
  EVENT_HISTORY_TYPE.PLAN_CANCEL,
  EVENT_HISTORY_TYPE.PLAN_START,
  EVENT_HISTORY_TYPE.PAYMENT,
  EVENT_HISTORY_TYPE.USAGE_MONTH,
];

export const isNonQuantitativeEvent = (type: EVENT_HISTORY_TYPE) =>
  NON_QUANTITATIVE_EVENT_TYPES.includes(type);

export const renderEventChange = (row: EventChangeSource) => {
  const tone = row.change < 0 ? "negative" : "positive";
  const prefix = tone === "positive" ? "↑" : "↓";
  const unit = getChangeUnit(row.planEventType);

  if (row.planEventType === EVENT_HISTORY_TYPE.USAGE_MONTH) {
    return "Yearly To Monthly";
  }

  if (row.planEventType === EVENT_HISTORY_TYPE.PLAN_START) {
    if (!row.description) return "-";
    return (
      planStartTermLabelMap[row.description as PLAN_START_TERM] ??
      row.description
    );
  }

  if (isNonQuantitativeEvent(row.planEventType)) {
    return "-";
  }

  if (unit === EVENT_HISTORY_CHANGE_UNIT.TEXT) {
    return <s.ChangeText $tone={tone}>{getOrgChangeLabel(row)}</s.ChangeText>;
  }

  const abs = Number.isFinite(row.change) ? Math.abs(row.change) : 0;

  return (
    <s.ChangeText $tone={tone}>
      {prefix} {formatNumber(abs)} {unit}
    </s.ChangeText>
  );
};

const eventOptions = [
  ...Object.values(EVENT_HISTORY_TYPE).map((value) => ({
    label: eventLabelMap[value],
    value,
  })),
];

export const buildEventToHistoryColumns = ({
  selectedAccount,
  queryInfo,
  onHeaderCell,
  onChangeType,
}: {
  selectedAccount: ProfileAccountInfo;
  queryInfo: BasePagination;
  onHeaderCell: (column: any) => any;
  onChangeType?: (value: string | undefined) => void;
}): TableColumnsType<EventHistoryDto> => {
  return [
    {
      key: "planEventType",
      title: (
        <TableColumnFilterTitle
          title="Event"
          filters={eventOptions}
          onClick={onChangeType}
        />
      ),
      dataIndex: "planEventType",
      render: renderEventType,
    },
    {
      key: "change",
      title: "Change",
      dataIndex: "change",
      render: (_: number | undefined, row: EventHistoryDto) =>
        renderEventChange(row),
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo!, "change"),
      onHeaderCell,
    },
    {
      key: "totalChannel",
      title: "Total Channels",
      dataIndex: "totalChannel",
      render: (value: number, row: EventHistoryDto) => {
        if (isNonQuantitativeEvent(row.planEventType)) {
          return "-";
        }
        return `${value} CH`;
      },
    },
    {
      key: "totalStorage",
      title: "Total Storage",
      dataIndex: "totalStorage",
      render: (value: number, row: EventHistoryDto) => {
        if (isNonQuantitativeEvent(row.planEventType)) {
          return "-";
        }
        return `${value} TB`;
      },
    },
    {
      key: "createdAt",
      title: "Date",
      dataIndex: "createdAt",
      render: (value: string) =>
        getAccountTimeFormat(value, selectedAccount, true),
      defaultSortOrder: "descend",
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo!, "createdAt"),
      onHeaderCell,
    },

    {
      key: "description",
      title: "Description",
      dataIndex: "description",
      render: (value: string, row: EventHistoryDto) => {
        if (row.planEventType === EVENT_HISTORY_TYPE.PAYMENT && row.amount) {
          return `$${formatPrice(row.amount / 100)}`;
        }
      },
    },
  ];
};

export const usePlanEventHistoryQuery = ({
  accountId,
  queryInfo,
  enabled,
  onSuccess,
}: {
  accountId: string;
  queryInfo: BasePagination;
  enabled: boolean;
  onSuccess?: (res: GetPlanEventHistoryResponse) => void;
}) => {
  return useQuery(
    ["getPlanEventHistory", accountId, queryInfo],
    () =>
      getPlanEventHistory({
        accountId,
        payload: queryInfo,
      }),
    {
      enabled,
      retry: 0,
      onSuccess,
    }
  );
};
